import { statusMap } from "./constants";
import AvailabilityDeadline from "./AvailabilityDeadline";
import Avatar from "@/components/Avatar";

const ROLE_BADGE = {
  admin: { label: "DT", cls: "bg-[#E5B85C]/20 text-[#E5B85C] border-[#E5B85C]/40" },
  coach: { label: "Coach", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
};

export default function PresenceTab({
  match,
  isCoach,
  myAvail,
  availByPlayer,
  present,
  setSelfAvailability,
  setPlayerAvailability,
}) {
  return (
    <>
      {/* Self-avail quick action — everyone (players, coaches, DT) can set their own availability */}
      {match.status !== "finished" && (
        <div className="no-print rounded-2xl bg-slate-900 border border-white/10 p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="text-[11px] font-mono-stat uppercase tracking-widest text-slate-400">
              Ma disponibilité
            </div>
            {match.availability_deadline && (
              <AvailabilityDeadline
                deadline={match.availability_deadline}
                locked={match.availability_locked}
              />
            )}
          </div>
          {match.availability_locked ? (
            <div
              data-testid="avail-locked-notice"
              className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm"
            >
              <b>Verrouillé</b> · Le délai (24h avant le match) est dépassé.{" "}
              {myAvail
                ? `Ta dernière réponse : ${statusMap[myAvail]?.label}.`
                : "Tu n'as pas répondu à temps."}{" "}
              Contacte l'entraîneur pour toute modification.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(statusMap).map(([k, v]) => (
                <button
                  key={k}
                  data-testid={`self-avail-${k}`}
                  onClick={() => setSelfAvailability(k)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-bold uppercase tracking-wide transition-colors ${
                    myAvail === k
                      ? `bg-${v.color}-500/30 border-${v.color}-500 text-${v.color}-200`
                      : `bg-${v.color}-500/10 border-${v.color}-500/30 text-${v.color}-300 hover:bg-${v.color}-500/20`
                  }`}
                >
                  <v.Icon className="w-4 h-4" />
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Presence roster — includes everyone (players, coaches, DT) */}
      <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="font-display font-bold text-lg uppercase">
            Effectif · {(match.players || []).filter((p) => !p.is_banned).length} membre(s)
          </div>
          <div className="text-xs font-mono-stat uppercase tracking-widest text-emerald-400">
            {present.length} présent(s)
          </div>
        </div>
        <div className="divide-y divide-white/5">
          {(match.players || [])
            .filter((p) => !p.is_banned)
            // Order: players first, then coaches, then admin
            .sort((a, b) => {
              const rank = { player: 0, coach: 1, admin: 2 };
              return (rank[a.role] ?? 3) - (rank[b.role] ?? 3);
            })
            .map((p) => {
              const a = availByPlayer[p.id];
              const meta = a && statusMap[a.status];
              const badge = ROLE_BADGE[p.role];
              return (
                <div
                  key={p.id}
                  data-testid={`presence-row-${p.id}`}
                  className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Avatar player={p} size={44} className="ring-1 ring-white/10" />
                    <div>
                      <div className="font-bold text-sm flex items-center gap-2 flex-wrap">
                        {p.name}
                        {p.jersey_number != null && (
                          <span className="text-[10px] font-mono-stat text-slate-500">
                            #{p.jersey_number}
                          </span>
                        )}
                        {badge && (
                          <span
                            className={`text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded border ${badge.cls}`}
                          >
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">{p.position || "—"}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {meta ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-${meta.color}-500/20 text-${meta.color}-300 border border-${meta.color}-500/30`}
                      >
                        <meta.Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500 font-mono-stat uppercase">
                        Sans réponse
                      </span>
                    )}
                    {isCoach && match.status !== "finished" && (
                      <div className="no-print flex gap-1">
                        {Object.entries(statusMap).map(([k, v]) => (
                          <button
                            key={k}
                            data-testid={`coach-avail-${p.id}-${k}`}
                            onClick={() => setPlayerAvailability(p.id, k)}
                            title={v.label}
                            className={`p-1.5 rounded border text-${v.color}-300 border-${v.color}-500/30 hover:bg-${v.color}-500/20`}
                          >
                            <v.Icon className="w-3.5 h-3.5" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          {(match.players || []).filter((p) => !p.is_banned).length === 0 && (
            <div className="p-6 text-center text-slate-500 text-sm">
              Aucun membre dans le club
            </div>
          )}
        </div>
      </div>
    </>
  );
}
